import { Navigate, useLocation, useParams } from "react-router-dom";

interface LegacyRedirectProps {
  /**
   * Função que recebe params + searchParams e retorna o novo path
   * (relativo ao root, ex.: "/inicio/visao-geral").
   */
  to: (ctx: {
    params: Record<string, string | undefined>;
    searchParams: URLSearchParams;
  }) => string;

  /**
   * Se true, propaga os search params extras (exceto os consumidos pela
   * função `to`). Default: true.
   */
  preserveSearch?: boolean;

  /**
   * Lista de chaves de query string que NÃO devem ser propagadas (porque
   * `to` já as transformou em segmento de path).
   */
  consumedKeys?: string[];
}

/**
 * Redirect 301-style usado para manter URLs antigas (`/dashboard/...?tab=...`)
 * funcionando após a migração para slugs limpos. Sempre preserva extras de
 * query string para não quebrar links com `?stripe_session=...`,
 * `?openOnboarding=1`, etc.
 */
export default function LegacyRedirect({
  to,
  preserveSearch = true,
  consumedKeys = [],
}: LegacyRedirectProps) {
  const params = useParams();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  let target = to({ params, searchParams });

  if (preserveSearch) {
    const remaining = new URLSearchParams();
    searchParams.forEach((value, key) => {
      if (!consumedKeys.includes(key)) remaining.append(key, value);
    });
    const remainingStr = remaining.toString();
    if (remainingStr) {
      target += target.includes("?") ? `&${remainingStr}` : `?${remainingStr}`;
    }
  }

  return <Navigate to={target} replace />;
}